import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LexLastSyncProvider } from './contexts/LexLastSyncContext';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './layout/MainLayout';
import Login from './pages/Login';
import Accueil from './pages/Accueil';
import DirectionTI from './pages/DirectionTI';
import DepartmentRetreats from './pages/DepartmentRetreats';
import CommentairesAmeliorationContinue from './pages/CommentairesAmeliorationContinue';
import DirecteurCompte from './pages/DirecteurCompte';
import Gestionnaires from './pages/Gestionnaires';
import AdminRH from './pages/AdminRH';
import SolutionsAffaires from './pages/SolutionsAffaires';
import SolutionsAffairesCybersecurite from './pages/SolutionsAffairesCybersecurite';
import SolutionsAffairesOffresCybersecurite from './pages/SolutionsAffairesOffresCybersecurite';
import SolutionsAffairesSubventionsFinancement from './pages/SolutionsAffairesSubventionsFinancement';
import IAAgentic from './pages/IAAgentic';
import AgentiqueTaches from './pages/AgentiqueTaches';
import AtlasDonneesBZ from './pages/AtlasDonneesBZ';
import Infonuagique from './pages/Infonuagique';
import ProgrammationIA from './pages/ProgrammationIA';
import OpenClawInstallationProcedure from './pages/OpenClawInstallationProcedure';
import Formation from './pages/Formation';
import GestionDocumentaire from './pages/GestionDocumentaire';
import GestionPartiesPrenantes from './pages/GestionPartiesPrenantes';
import DemoAI from './pages/DemoAI';
import Concepts from './pages/Concepts';
import Comites from './pages/Comites';
import ComiteCE from './pages/ComiteCE';
import LacEpauleSeptembre2026 from './pages/LacEpauleSeptembre2026';
import ComiteCA from './pages/ComiteCA';
import ComitePRIsME from './pages/ComitePRIsME';
import ComiteSecurite from './pages/ComiteSecurite';
import ComiteRH from './pages/ComiteRH';
import BZTelecom from './pages/BZTelecom';
import Projets from './pages/Projets';
import ProjetsV2, { MonAgentPage } from './pages/ProjetsV2';
import ProjetEH from './pages/ProjetEH';
import CatalogueActifs from './pages/CatalogueActifs';
import ContenuEnRevision from './pages/ContenuEnRevision';
import { useAuth } from './contexts/AuthContext';
import LoiSignaturePage from './pages/public/LoiSignaturePage';

function VentesReunionOnlyGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const email = user?.email?.toLowerCase();
  const isMelissaMorency = email === 'melissa.morency@bzinc.ca';
  const isVentesReunionOnlyUser =
    email === 'jessica.pratt@bzinc.ca' ||
    email === 'rosalie.emond@bzinc.ca' ||
    email === 'edita.puhovac@bzinc.ca' ||
    email === 'tim.gaudet@bzinc.ca' ||
    email === 'michael.roy@bzinc.ca' ||
    email === 'zachary.seguin@bzinc.ca';

  if (isMelissaMorency) {
    const allowedExactPaths = new Set([
      '/direction-ti',
      '/direction-ti/lac-a-epaule-decembre-2026',
      '/directeur-compte',
      '/solutions-affaires',
      '/solutions-affaires/cybersecurite',
      '/solutions-affaires/offres-de-cybersecurite',
      '/solutions-affaires/subventions-financement',
      '/ia-agentic',
      '/ia-agentic/taches',
      '/ia-agentic/atlas-donnees-bz',
      '/cybersecurite',
      '/programmation-ia',
      '/gestion-documentaire',
    ]);

    if (!allowedExactPaths.has(location.pathname)) {
      return <Navigate to="/directeur-compte" replace />;
    }

    return <>{children}</>;
  }

  if (!isVentesReunionOnlyUser) return <>{children}</>;

  const allowedPath = '/directeur-compte';
  if (location.pathname !== allowedPath) {
    return <Navigate to={allowedPath} replace />;
  }

  return <>{children}</>;
}

function CyberOnlyGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const email = user?.email?.toLowerCase();
  const isCyberOnlyUser =
    email === 'mikael.drouin-bouchard@bzinc.ca' ||
    email === 'philippe.bolduc@bzinc.ca';

  if (!isCyberOnlyUser) return <>{children}</>;

  // La section a été renommée "Solutions d’affaires" mais on supporte encore l'ancienne route
  // pour éviter de casser les bookmarks / liens existants.
  // Inclut /projets : la page Projets applique elle-même PROJETS_ALLOWED_EMAILS (ex. refus pour les autres cyber-only).
  const allowed = new Set([
    '/solutions-affaires',
    '/solutions-affaires/cybersecurite',
    '/solutions-affaires/offres-de-cybersecurite',
    '/solutions-affaires/subventions-financement',
    '/ia-agentic',
    '/ia-agentic/taches',
    '/ia-agentic/atlas-donnees-bz',
    '/cybersecurite',
    '/gestion-documentaire',
    '/projets',
    '/projets-v2',
    '/projets-legacy',
  ]);
  const isAllowed = allowed.has(location.pathname) || location.pathname.startsWith('/projets-v2/');
  if (!isAllowed) {
    return <Navigate to="/solutions-affaires" replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <LexLastSyncProvider>
      <Router>
        <Routes>
          {/* Routes publiques */}
          <Route path="/login" element={<Login />} />
          <Route path="/preview-projets" element={<ProjetsV2 previewMode />} />
          <Route path="/preview-projets/:projectId" element={<ProjetsV2 previewMode />} />
          <Route path="/loi-sign/:token" element={<LoiSignaturePage />} />
          
          {/* Routes protégées */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <VentesReunionOnlyGuard>
                  <CyberOnlyGuard>
                    <MainLayout>
                      <Routes>
                      <Route path="/" element={<Accueil />} />
                      <Route path="/direction-ti" element={<DirectionTI />} />
                      <Route path="/direction-ti/lac-a-epaule-decembre-2026" element={<Navigate to="/comites/lac-a-epaule/ti" replace />} />
                      <Route path="/comites/lac-a-epaule" element={<DepartmentRetreats />} />
                      <Route path="/comites/lac-a-epaule/:department" element={<DepartmentRetreats />} />
                      <Route path="/direction-ti/commentaires" element={<CommentairesAmeliorationContinue />} />
                      <Route path="/comites" element={<Comites />} />
                      <Route path="/comite-ce" element={<ComiteCE />} />
                      <Route path="/comite-ce/lac-epaule-septembre-2026" element={<LacEpauleSeptembre2026 />} />
                      <Route path="/comite-ca" element={<ComiteCA />} />
                      <Route path="/comite-prisme" element={<ComitePRIsME />} />
                      <Route path="/comite-securite" element={<ComiteSecurite />} />
                      <Route path="/comite-rh" element={<ComiteRH />} />
                      <Route path="/directeur-compte" element={<DirecteurCompte />} />
                      <Route path="/gestionnaires" element={<Gestionnaires />} />
                      <Route path="/admin-rh" element={<AdminRH />} />
                      <Route path="/cybersecurite" element={<Navigate to="/solutions-affaires/cybersecurite" replace />} />
                      <Route path="/solutions-affaires" element={<SolutionsAffaires />} />
                      <Route path="/solutions-affaires/cybersecurite" element={<SolutionsAffairesCybersecurite />} />
                      <Route path="/solutions-affaires/offres-de-cybersecurite" element={<SolutionsAffairesOffresCybersecurite />} />
                      <Route path="/solutions-affaires/subventions-financement" element={<SolutionsAffairesSubventionsFinancement />} />
                      <Route path="/ia-agentic" element={<IAAgentic />} />
                      <Route path="/ia-agentic/taches" element={<AgentiqueTaches />} />
                      <Route path="/ia-agentic/atlas-donnees-bz" element={<AtlasDonneesBZ />} />
                      <Route path="/infonuagique" element={<Infonuagique />} />
                      <Route path="/programmation-ia" element={<ProgrammationIA />} />
                      <Route path="/programmation-ia/openclaw-installation" element={<OpenClawInstallationProcedure />} />
                      <Route path="/bz-telecom" element={<BZTelecom />} />
                      <Route path="/projets" element={<Projets />} />
                      <Route path="/projets-v2" element={<ProjetsV2 />} />
                      <Route path="/projets-v2/:projectId" element={<ProjetsV2 />} />
                      <Route path="/mon-agent" element={<MonAgentPage />} />
                      <Route path="/projets-legacy" element={<Projets />} />
                      <Route path="/projet-eh" element={<ProjetEH />} />
                      <Route path="/formation" element={<Formation />} />
                      <Route path="/gestion-documentaire" element={<GestionDocumentaire />} />
                      <Route path="/catalogue-actifs" element={<CatalogueActifs />} />
                      <Route path="/contenu-en-revision" element={<ContenuEnRevision />} />
                      <Route path="/gestion-parties-prenantes" element={<GestionPartiesPrenantes />} />
                      <Route path="/demo-ai" element={<DemoAI />} />
                      <Route path="/concepts" element={<Concepts />} />
                      </Routes>
                    </MainLayout>
                  </CyberOnlyGuard>
                </VentesReunionOnlyGuard>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      </LexLastSyncProvider>
    </AuthProvider>
  );
}

export default App;
